import { Fragment } from 'react';

import { Add16, Bin16 } from '@ingrid/iconography/react';
import { Button, DotBadge, IconButton, Spinner, styled } from '@ingrid/luterkort-react';

import { CodeEditor } from '~src/components/CodeEditor/CodeEditor';
import { SimpleSelect } from '~src/components/SimpleSelect/SimpleSelect';
import { useTranslation } from '~src/i18n/use-translation';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';
import { useDetailedShipmentMutation } from '~src/pages/shipment/mutations';
import { generateId } from '~src/utils/generate-id';
import { toPrettyJson, tryParseJson } from '~src/utils/json';

import { useShipmentSchema } from '../hooks';
import { useConfiguratorContext } from './configurator-context';
import { RunTestButton } from './run-test-button';
import { ShipmentSelectModal } from './shipment-select-modal';
import { Footer, Wrapper } from './test-runner-layout';

const LoadingOverlay = styled('div', {
  justifyContent: 'center',
  position: 'absolute',
  alignItems: 'center',
  background: 'white',
  display: 'flex',
  height: '100%',
  width: '100%',
  zIndex: 10,
  left: 0,
  top: 0,

  variants: {
    mode: {
      dark: {
        background: 'var(--grey-18)',
      },
    },
  },
});

export function TestRunnerInput() {
  const {
    hasInvalidRawTestShipments,
    setRawTestShipmentId,
    setRawTestShipments,
    rawTestShipmentId,
    rawTestShipments,
    rawTestShipment,
  } = useConfiguratorContext();

  const { t } = useTranslation();

  const lookupMutation = useDetailedShipmentMutation();
  const shipmentSchema = useShipmentSchema();
  const tracking = useTracking();

  function handleChangeTestShipment(value: string | undefined) {
    if (!value) {
      return;
    }

    setRawTestShipments(prev => {
      const updated = { ...prev };
      updated[rawTestShipmentId] = value;
      return updated;
    });
  }

  function handleDeleteTestShipment() {
    setRawTestShipments(prev => {
      const newValue: Record<string, string> = {};

      for (const [key, value] of Object.entries(prev)) {
        if (key === rawTestShipmentId) {
          continue;
        }

        newValue[key] = value;
      }

      return newValue;
    });

    const availableShipmentIds = Object.keys(rawTestShipments);
    const currentShipmentIndex = availableShipmentIds.indexOf(rawTestShipmentId);
    const previousShipmentIndex = Math.max(currentShipmentIndex - 1, 0);

    setRawTestShipmentId(availableShipmentIds[previousShipmentIndex]);
  }

  function handleAddTestShipment() {
    const newId = generateId();

    setRawTestShipments(p => ({ ...p, [newId]: '{}' }));
    setRawTestShipmentId(newId);
  }

  async function handlePrefillTestShipment(id: string) {
    try {
      const shipment = await lookupMutation.mutateAsync({ id });
      const value = shipmentSchema.parse(shipment);

      setRawTestShipments(p => ({ ...p, [rawTestShipmentId]: toPrettyJson(value) }));
      tracking.capture(TrackingEventEnum.ConfiguratorPrefillShipmentCompleted);
    } catch (e) {
      tracking.capture(TrackingEventEnum.ConfiguratorPrefillShipmentFailed);
      throw e;
    }
  }

  return (
    <Wrapper>
      <div style={{ flex: 1, position: 'relative' }}>
        <CodeEditor
          options={{ minimap: { enabled: false }, automaticLayout: true }}
          path="inmemory://model/shipment.json"
          onChange={handleChangeTestShipment}
          value={rawTestShipment}
          key={rawTestShipmentId}
          language="json"
        />

        {lookupMutation.isPending && (
          <LoadingOverlay>
            <Spinner />
          </LoadingOverlay>
        )}
      </div>

      <Footer>
        <div style={{ display: 'flex', gap: '8px' }}>
          <SimpleSelect
            options={Object.keys(rawTestShipments).map((key, index) => [
              key,
              t('bookingRules.edit.test-shipment-option', { id: index + 1 }),
            ])}
            renderOptionLabel={([value, label]) => {
              const valid = shipmentSchema.safeParse(tryParseJson(rawTestShipments[value], false)).success;
              return valid ? <Fragment>{label}</Fragment> : <DotBadge variant="critical">{label}</DotBadge>;
            }}
            onValueChange={([value]) => setRawTestShipmentId(value)}
            invalid={hasInvalidRawTestShipments}
            value={[rawTestShipmentId]}
          />

          <IconButton
            onClick={handleAddTestShipment}
            aria-label={t('common.add')}
            style={{ flexShrink: 0 }}
            variant="secondary"
          >
            <Add16 />
          </IconButton>

          <IconButton
            disabled={Object.keys(rawTestShipments).length < 2}
            onClick={handleDeleteTestShipment}
            aria-label={t('common.delete')}
            style={{ flexShrink: 0 }}
            variant="secondary"
          >
            <Bin16 />
          </IconButton>

          <ShipmentSelectModal
            onValueChange={([shipmentId]) => handlePrefillTestShipment(shipmentId)}
            trigger={
              <Button style={{ flexShrink: 0 }} variant="secondary">
                {t('bookingRules.edit.pre-fill.title')}
              </Button>
            }
          />
        </div>

        <RunTestButton i18nKey="bookingRules.edit.run-test" />
      </Footer>
    </Wrapper>
  );
}
